'use client';

import { useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/navigation';

/**
 * Per-locale route error boundary. Next 16 mounts this client component
 * when any descendant of `app/[locale]/` throws during render or in a
 * client effect. Without it, production users land on the framework's
 * default error page — no branding, no localised copy, no recovery path.
 *
 * `reset` is provided by Next and re-attempts to render the segment.
 * Useful for transient failures (network blip, missing image). For hard
 * failures the user can still navigate home.
 */
export default function LocaleError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const t = useTranslations('Error');

  useEffect(() => {
    // Log to the browser console so the user / dev tools see the stack;
    // a real production deploy should also forward to an external sink
    // (Sentry, Logtail, etc.) — left as a follow-up because no provider
    // is configured in the repo yet.
     
    console.error('Route error:', error);
  }, [error]);

  return (
    <main className="legal-page" role="alert" aria-live="assertive">
      <header>
        <span className="legal-page__tag">Error</span>
        <h1>{t('title')}</h1>
      </header>
      <p>{t('body')}</p>
      {error.digest ? (
        <p>
          <code>{error.digest}</code>
        </p>
      ) : null}
      <p style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
        <button
          type="button"
          onClick={reset}
          className="legal-page__action"
          style={{
            background: 'transparent',
            border: '1px solid currentColor',
            padding: '0.6rem 1.2rem',
            fontFamily: 'var(--font-mono), monospace',
            fontSize: '0.78rem',
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: 'var(--color-accent)',
            cursor: 'pointer',
          }}
        >
          {t('retry')} →
        </button>
        <Link
          href="/"
          style={{
            padding: '0.6rem 1.2rem',
            fontFamily: 'var(--font-mono), monospace',
            fontSize: '0.78rem',
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: 'var(--color-muted)',
            textDecoration: 'none',
            border: '1px solid color-mix(in srgb, var(--color-muted-foreground) 30%, transparent)',
          }}
        >
          {t('home')} →
        </Link>
      </p>
    </main>
  );
}
